import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Stethoscope, Search, Plus, Clock, Mail, Phone, Edit2, Trash2, MapPin, AlertCircle, Eye } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useAdmin } from '@/contexts/AdminContext';
import SiteFilter from '@/components/admin/SiteFilter';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

const SITES = {
  'shihab_romeed': 'SR',
  'specialist_dental_london': 'SDL',
  'london_dental_academy': 'LDA'
};

export default function AdminProviders() {
  const { apiFetch } = useAdmin();
  const navigate = useNavigate();
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [siteFilter, setSiteFilter] = useState('all');
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    loadProviders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteFilter]);

  const loadProviders = () => {
    setLoading(true);
    const query = siteFilter !== 'all' ? `?siteKey=${siteFilter}` : '';
    apiFetch(`/providers${query}`)
      .then(r => r.json())
      .then(d => setProviders(d.providers || []))
      .catch(() => toast.error('Failed to load providers'))
      .finally(() => setLoading(false));
  };

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      const res = await apiFetch(`/providers/${deleting.id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Failed to delete provider');
      toast.success('Provider deactivated successfully');
      loadProviders();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setDeleting(null);
    }
  };

  const filtered = providers.filter(p => 
    !search || (p.name || '').toLowerCase().includes(search.toLowerCase()) || 
    (p.email || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-8 max-w-7xl mx-auto pb-12">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-stone-400">Team</p>
          <h2 className="text-2xl lg:text-3xl font-playfair font-medium text-stone-900 tracking-tight mt-1">Providers</h2>
          <p className="text-stone-500 text-sm mt-1">Manage clinicians, their profiles, and their schedules.</p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/admin/providers/new">
            <Button className="h-10 gap-2 bg-stone-900 hover:bg-stone-800 text-white shadow-sm px-5">
              <Plus className="w-4 h-4" />
              Add Provider
            </Button>
          </Link>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-4 justify-between bg-stone-50 p-2 rounded-xl border border-stone-200">
        <SiteFilter value={siteFilter} onChange={setSiteFilter} className="w-full sm:w-auto" />
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 w-4 h-4" />
          <Input 
            placeholder="Search providers..." 
            value={search} 
            onChange={e => setSearch(e.target.value)} 
            className="pl-9 h-10 border-stone-200 bg-white" 
          />
        </div>
      </div>

      {loading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-[280px] rounded-2xl" />)}
        </div>
      ) : filtered.length === 0 ? (
        <Card className="border-stone-200 border-dashed">
          <CardContent className="py-20 text-center">
            <Stethoscope className="w-12 h-12 text-stone-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-stone-900">No Providers Found</h3>
            <p className="text-stone-500 text-sm mt-1 mb-6">
              {search ? 'Try adjusting your search terms.' : 'Get started by adding your first clinician.'}
            </p>
            {!search && (
              <Link to="/admin/providers/new">
                <Button variant="outline" className="h-9 gap-2"><Plus className="w-4 h-4" /> Add Provider</Button>
              </Link>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          <AnimatePresence>
            {filtered.map((p, i) => (
              <motion.div 
                key={p.id} 
                initial={{ opacity: 0, scale: 0.95 }} 
                animate={{ opacity: 1, scale: 1 }} 
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.2, delay: i * 0.03 }}
              >
                <Card className="group h-full border-stone-200 shadow-sm hover:border-stone-300 hover:shadow-md transition-all duration-300 overflow-hidden flex flex-col relative">
                  <CardContent className="p-0 flex flex-col flex-1">
                    {/* Header */}
                    <div className="p-6 border-b border-stone-100 bg-gradient-to-b from-stone-50 to-white">
                      <div className="flex justify-between items-start mb-4">
                        <div className="h-14 w-14 rounded-2xl bg-stone-900 flex items-center justify-center text-white font-playfair font-medium text-xl shadow-inner">
                          {(p.name || 'P').charAt(0)}
                        </div>
                        <div className="flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Link to={`/admin/providers/${p.id}/edit`}>
                            <Button size="icon" variant="ghost" className="h-8 w-8 text-stone-400 hover:text-stone-900 bg-white border border-stone-200 shadow-sm">
                              <Edit2 className="w-3.5 h-3.5" />
                            </Button>
                          </Link>
                          <Button size="icon" variant="ghost" onClick={() => setDeleting(p)} className="h-8 w-8 text-stone-400 hover:text-red-600 hover:bg-red-50 bg-white border border-stone-200 shadow-sm">
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                      
                      <div>
                        <h3 className="font-playfair font-medium text-lg text-stone-900 truncate">{p.name}</h3>
                        <p className="text-xs font-medium text-stone-500 uppercase tracking-wider mt-1 truncate">
                          {p.title || 'Clinician'}
                        </p>
                      </div>
                    </div>

                    {/* Body */}
                    <div className="p-6 flex-1 flex flex-col gap-5">
                      {p.specialties && p.specialties.length > 0 && (
                        <div>
                          <p className="text-[10px] font-semibold text-stone-400 uppercase tracking-wider mb-2">Specialties</p>
                          <div className="flex flex-wrap gap-1.5">
                            {p.specialties.map((spec, idx) => (
                              <span key={idx} className="px-2 py-1 bg-stone-100 text-stone-600 text-[11px] font-medium rounded-md">
                                {spec}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="space-y-2 mt-auto">
                        <div className="flex items-center gap-2.5 text-sm text-stone-600">
                          <MapPin className="w-4 h-4 text-stone-400 shrink-0" />
                          <div className="flex gap-1.5 flex-wrap">
                            {p.sites?.length > 0 ? p.sites.map(s => (
                              <span key={s} className="px-1.5 py-0.5 bg-stone-100 border border-stone-200 text-stone-700 text-[10px] font-medium rounded uppercase">
                                {SITES[s] || s}
                              </span>
                            )) : <span className="text-xs text-red-500 flex items-center gap-1"><AlertCircle className="w-3 h-3"/> No sites assigned</span>}
                          </div>
                        </div>
                        {p.email && (
                          <div className="flex items-center gap-2.5 text-sm text-stone-600">
                            <Mail className="w-4 h-4 text-stone-400 shrink-0" />
                            <span className="truncate">{p.email}</span>
                          </div>
                        )}
                        {p.phone && (
                          <div className="flex items-center gap-2.5 text-sm text-stone-600">
                            <Phone className="w-4 h-4 text-stone-400 shrink-0" />
                            <span>{p.phone}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Footer Actions */}
                    <div className="p-4 border-t border-stone-100 bg-stone-50 flex gap-2">
                      <Link to={`/admin/providers/${p.id}`} className="flex-1">
                        <Button variant="outline" className="w-full h-9 gap-2 text-stone-700 bg-white border-stone-200 hover:bg-stone-50 hover:text-stone-900 transition-colors">
                          <Eye className="w-3.5 h-3.5" /> View Profile
                        </Button>
                      </Link>
                      <Link to={`/admin/schedule/${p.id}`} className="flex-1">
                        <Button variant="outline" className="w-full h-9 gap-2 text-stone-700 bg-white border-stone-200 hover:bg-stone-50 hover:text-stone-900 transition-colors">
                          <Clock className="w-3.5 h-3.5" /> Schedule
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate Provider</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to deactivate <strong>{deleting?.name}</strong>? They will no longer be able to log in or receive new bookings.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white">
              Deactivate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
